import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { ShieldCheck } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import { useVaultStore } from '../store/vaultStore';
import './Vault.css';

/**
 * Standalone vault card 7.1: the vault grew up (extension without the app,
 * Android, vault.apiboo.com), so ask for a backup ONCE. Non-blocking, a line
 * above the list — not a launch modal: the cloud-copy question (6.3') is
 * already the one modal at that moment.
 *   Export backup     → the existing encrypted export (then never again)
 *   I have a backup   → never again on this device
 *   Remind me later   → back in 7 days
 * Shown only for an unlocked vault that holds something. Remembered per
 * device (localStorage: no secret in it, only the choice and a date).
 */
const KEY = 'apiboo.vault.upgradeNotice';
const LATER_MS = 7 * 24 * 60 * 60 * 1000;

export function upgradeNoticeDue(now = Date.now()) {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return true;
    const v = JSON.parse(raw);
    if (v && v.state === 'done') return false;
    if (v && v.state === 'later' && typeof v.until === 'number') return now >= v.until;
    return true;
  } catch {
    return true;
  }
}

function remember(state, now = Date.now()) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state === 'later' ? { state, until: now + LATER_MS } : { state }));
  } catch {
    // a blocked storage only means the line comes back next time
  }
}

export default function VaultUpgradeNotice({ onExport }) {
  const { t } = useTranslation();
  const status = useVaultStore((s) => s.status);
  const items = useVaultStore((s) => s.items);
  const [due, setDue] = useState(() => upgradeNoticeDue());
  const [cloud, setCloud] = useState(false);

  const show = isTauri() && due && !!status?.unlocked && Array.isArray(items) && items.length > 0;

  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    invoke('vault_sync_status')
      .then((s) => alive && setCloud(!!(s && s.enrolled)))
      .catch(() => alive && setCloud(false));
    return () => {
      alive = false;
    };
  }, [show]);

  if (!show) return null;

  const close = (state) => {
    remember(state);
    setDue(false);
  };

  return (
    <div className="vault-pass-banner" data-testid="vault-upgrade-notice" role="status">
      <ShieldCheck size={15} className="vault-pass-banner-icon" />
      <div className="vault-pass-banner-texts">
        <span className="vault-pass-banner-title">{t('vault.upgradeNoticeTitle', 'Your vault is getting an upgrade')}</span>
        <span className="vault-pass-banner-text">
          {t(
            'vault.upgradeNoticeBody',
            'It will work in the browser extension without this app, on Android and at vault.apiboo.com. It stays end-to-end encrypted — Apiboo can’t read it; only your master password opens it. Please export a backup first.'
          )}
          {cloud ? ` ${t('vault.upgradeNoticeCloud', 'An encrypted copy is already stored in your Apiboo cloud.')}` : ''}
        </span>
      </div>
      <div className="vault-pass-banner-actions">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
          onClick={() => {
            close('done');
            onExport();
          }}
        >
          {t('vault.upgradeNoticeExport', 'Export backup')}
        </button>
        <button type="button" className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm" onClick={() => close('done')}>
          {t('vault.upgradeNoticeHave', 'I have a backup')}
        </button>
        <button type="button" className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm" onClick={() => close('later')}>
          {t('vault.upgradeNoticeLater', 'Remind me later')}
        </button>
      </div>
    </div>
  );
}
